import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { rateCalculatorAPI } from '../services/api.jsx';
import { rateClientsAPI } from '../services/rateClientsAPI';
import ProtectedRoute from '../components/ProtectedRoute';
import ClientModal from '../components/ClientModal';
import CommissionModal from '../components/CommissionModal';
import LoadedCostModal from '../components/LoadedCostModal';
import CalculationNotesModal from '../components/CalculationNotesModal';
import '../styles/RateCalculator.css';

const RateCalculator = () => {
  const { hasAnyPermission } = useAuth();

  const [showNotesModal, setShowNotesModal] = useState(false);
  const canManageClients = hasAnyPermission(['rate-clients:write']);
  
  const toPercentValue = (val) => {
    const num = parseFloat(val);
    if (!Number.isFinite(num)) return null;
    // If value is less than 1, it's stored as decimal (0.40 = 40%)
    // If value is 1 or greater, it's already a percentage (8 = 8%)
    return num < 1 ? num * 100 : num;
  };

  const formatPercentDisplay = (val) => {
    const num = toPercentValue(val);
    return num !== null ? `${num.toFixed(2)}%` : '—';
  };

  const getResultValueStyle = (value) => {
    return value < 0 ? { color: 'red' } : {};
  };

  // Determine which result field should be highlighted based on what inputs were provided
  const getHighlightedResultStyle = (resultField) => {
    const billRateProvided = formData.targetBillRate !== '';
    const payRateProvided = formData.employmentType === 'fulltime' ? formData.annualSalary !== '' : formData.targetPayRate !== '';
    const gpProvided = formData.targetGP !== '' || formData.allowedMarkup !== '';

    // If only pay rate was provided → highlight bill rate (it was calculated)
    if (payRateProvided && !billRateProvided && !gpProvided && resultField === 'billRate') {
      return { borderLeftColor: '#107c10', background: '#f0f8f3' };
    }
    // If only bill rate was provided → highlight pay rate (it was calculated)
    if (billRateProvided && !payRateProvided && !gpProvided && resultField === 'payRate') {
      return { borderLeftColor: '#107c10', background: '#f0f8f3' };
    }
    // If pay rate + GP provided → highlight bill rate (calculated from GP)
    if (payRateProvided && gpProvided && !billRateProvided && resultField === 'billRate') {
      return { borderLeftColor: '#107c10', background: '#f0f8f3' };
    }
    // If bill rate + GP provided → highlight pay rate (calculated from GP)
    if (billRateProvided && gpProvided && !payRateProvided && resultField === 'payRate') {
      return { borderLeftColor: '#107c10', background: '#f0f8f3' };
    }
    // Default: blue styling
    return { borderLeftColor: '#0078d4', background: '#f8f9fa' };
  };

  // Determine the text color for result values (green if highlighted, otherwise blue)
  const getHighlightedResultValueColor = (resultField) => {
    const billRateProvided = formData.targetBillRate !== '';
    const payRateProvided = formData.employmentType === 'fulltime' ? formData.annualSalary !== '' : formData.targetPayRate !== '';
    const gpProvided = formData.targetGP !== '' || formData.allowedMarkup !== '';

    if ((payRateProvided && !billRateProvided && !gpProvided && resultField === 'billRate') ||
        (billRateProvided && !payRateProvided && !gpProvided && resultField === 'payRate') ||
        (payRateProvided && gpProvided && !billRateProvided && resultField === 'billRate') ||
        (billRateProvided && gpProvided && !payRateProvided && resultField === 'payRate')) {
      return { color: '#107c10' };
    }
    return { color: '#0078d4' };
  };

  const [clients, setClients] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showLoadedCostModal, setShowLoadedCostModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, CAD: 1, INR: 1 });
  const [loadingRates, setLoadingRates] = useState(false);
  const [loadedCostMultiplier, setLoadedCostMultiplier] = useState(1.0);
  const [loadedCostComponents, setLoadedCostComponents] = useState({
    pto: 0,
    benefits: 0,
    taxes: 0,
    h1bTransferFees: 0,
    otherFees: 0
  });
  const [loadedCostPercent, setLoadedCostPercent] = useState(0);
  const [loadedCostFees, setLoadedCostFees] = useState([{ id: 1, name: '', amount: 0 }]);
  
  const [formData, setFormData] = useState({
    client: '',
    employmentType: 'c2c',
    discount: '',
    targetBillRate: '',
    targetPayRate: '',
    annualSalary: '',
    targetGP: '',
    allowedMarkup: ''
  });

  const [calculations, setCalculations] = useState({
    monthlyCommission: 0,
    markupPercent: 0,
    billRateBeforeDiscount: 0,
    billRate: 0,
    payRate: 0,
    loadedCost: 0,
    gpPercent: 0,
    grossProfit: 0,
    gpPerHour: 0,
    hasDefinedMarkup: false,
    targetGP: 0
  });

  useEffect(() => {
    loadClientAndDiscountData();
    fetchExchangeRates();
  }, []);

  // Update form data when client list changes (e.g., after editing a client)
  useEffect(() => {
    if (formData.client && clients.length > 0) {
      const selectedClient = clients.find(c => c.ClientId === parseInt(formData.client));
      if (selectedClient && selectedClient.TotalDiscounts) {
        const discountPercent = toPercentValue(selectedClient.TotalDiscounts);
        const updatedData = { ...formData, discount: discountPercent ?? '' };
        setFormData(updatedData);
        calculateValues(updatedData);
      }
    }
  }, [clients]);

  // Recalculate when employment type or client changes to apply new markup
  useEffect(() => {
    if (clients.length > 0) {
      calculateValues(formData, null, selectedCurrency);
    }
  }, [formData.employmentType, formData.client, formData.targetPayRate, formData.targetBillRate, formData.annualSalary, formData.discount, formData.targetGP, formData.allowedMarkup, loadedCostMultiplier, selectedCurrency, loadedCostFees]);

  // Recalculate loaded cost multiplier when fees change
  useEffect(() => {
    if (loadedCostFees.length > 0) {
      const basePercent = (formData.employmentType === 'w2 hourly' || formData.employmentType === 'fulltime' || formData.employmentType === 'w2 deel') ? 18 : 0;
      let additionalPercent = 0;
      
      // Use either the entered pay rate or the calculated pay rate from Bill Rate + GP%
      const payRateForCalculation = formData.targetPayRate > 0 ? formData.targetPayRate : calculations.payRate;
      const annualHours = 2080; // Treat fee inputs as annual amounts
      
      // Only calculate additional percent if we have a valid pay rate
      if (payRateForCalculation > 0) {
        additionalPercent = loadedCostFees.reduce((sum, fee) => {
          const hourlyAmount = fee.amount / annualHours;
          const hourlyPercent = (hourlyAmount / payRateForCalculation) * 100;
          return sum + hourlyPercent;
        }, 0);
      }
      
      const totalPercent = basePercent + additionalPercent;
      const newMultiplier = 1 + (totalPercent / 100);
      setLoadedCostMultiplier(newMultiplier);
      setLoadedCostPercent(totalPercent);
      // Trigger recalculation with new multiplier
      calculateValues(formData, newMultiplier, selectedCurrency);
    }
  }, [loadedCostFees, formData.targetPayRate, formData.employmentType, selectedCurrency, calculations.payRate]);

  const loadClientAndDiscountData = async () => {
    try {
      setLoading(true);
      const clientsResponse = await rateClientsAPI.getClients();
      setClients(clientsResponse.data || []);
      // Discounts removed - user can manually enter discount percentage
      setDiscounts([]);
    } catch (err) {
      console.error('Failed to load calculator data', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      setLoadingRates(true);
      // Proxy through backend to avoid CORS issues
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${apiUrl}/rate-calculator/exchange-rates`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch rates');
      const data = await response.json();
      
      if (data.rates && typeof data.rates === 'object') {
        const rates = {
          USD: 1,
          CAD: parseFloat(data.rates.CAD) || 1.35,
          INR: parseFloat(data.rates.INR) || 83
        };
        setExchangeRates(rates);
      } else {
        throw new Error('Invalid rate data');
      }
    } catch (err) {
      console.error('Failed to fetch exchange rates, using fallback:', err);
      setExchangeRates({ USD: 1, CAD: 1.35, INR: 83 });
    } finally {
      setLoadingRates(false);
    }
  };

  const handleClientModalClose = () => {
    setShowClientModal(false);
    loadClientAndDiscountData(); // Refresh clients when modal closes
  };

  const handleLoadedCostSave = (multiplier, percent, components, fees) => {
    // Update loaded cost state
    setLoadedCostMultiplier(multiplier);
    setLoadedCostPercent(percent);
    setLoadedCostComponents(components);
    setLoadedCostFees(fees);
    
    // Trigger recalculation by calling calculateValues directly with the new multiplier
    // This ensures all dependent values are recalculated synchronously
    calculateValues(formData, multiplier);
  };

  const calculateValues = (data, customMultiplier = null, currencyCode = selectedCurrency) => {
    let billRate = parseFloat(data.targetBillRate) || 0;
    let payRate = parseFloat(data.targetPayRate) || 0;
    
    // Store original manually entered values to preserve precision
    const originalBillRate = parseFloat(data.targetBillRate) || 0;
    const originalPayRate = parseFloat(data.targetPayRate) || 0;
    const targetGP = parseFloat(data.targetGP) || 0;
    const allowedMarkup = parseFloat(data.allowedMarkup) || 0;
    
    // If payRate is not entered but we have a calculated one, use it
    // This is important when Bill Rate is set and we're recalculating with a new multiplier
    // When customMultiplier is provided (loaded cost change), preserve the existing pay rate
    // and let the loaded cost change, which will cause GP% to recalculate
    if (customMultiplier !== null && payRate === 0 && calculations.payRate > 0 && billRate > 0) {
      payRate = calculations.payRate;
    }
    
    // Convert annual salary to hourly rate for full-time positions
    if (data.employmentType === 'fulltime' && data.annualSalary) {
      const annualSalary = parseFloat(data.annualSalary) || 0;
      if (annualSalary > 0) {
        payRate = annualSalary / 2080; // 2080 hours per year
      }
    }
    
    const discount = parseFloat(data.discount) || 0;

    // Loaded cost multiplier - can be applied to W2, Full-Time, and C2C
    // W2/Full-Time has a base 18%, C2C starts from 0%
    let LOADED_COST_MULTIPLIER = 1.0;
    if (data.employmentType === 'w2 hourly' || data.employmentType === 'fulltime' || data.employmentType === 'w2 deel') {
      // For W2/fulltime: use custom multiplier if set, otherwise use state multiplier, or default to 1.18 (18% base)
      LOADED_COST_MULTIPLIER = customMultiplier !== null ? customMultiplier : (isNaN(loadedCostMultiplier) || loadedCostMultiplier <= 1.0 ? 1.18 : loadedCostMultiplier);
    } else if (data.employmentType === 'c2c' || data.employmentType === 'c2c deel' || data.employmentType === 'c2c canada') {
      // For C2C and C2C/Deel: use custom multiplier if set, otherwise use state multiplier (defaults to 1.0, no base)
      LOADED_COST_MULTIPLIER = customMultiplier !== null ? customMultiplier : (isNaN(loadedCostMultiplier) ? 1.0 : loadedCostMultiplier);
    }
    // Allowed Markup % is a no-burden calculation: ignore W2/C2C loaded cost fees entirely
    if (allowedMarkup > 0) {
      LOADED_COST_MULTIPLIER = 1.0;
    }
    let loadedCost = payRate * LOADED_COST_MULTIPLIER;

    // Get client and their markup for the selected employment type
    const selectedClient = data.client ? clients.find(c => c.ClientId === parseInt(data.client)) : null;
    let clientMarkupPercent = null;
    let hasDefinedMarkup = false; // track if the client actually provided a markup value
    if (selectedClient) {
      if (data.employmentType === 'c2c' || data.employmentType === 'c2c deel' || data.employmentType === 'c2c canada') {
        const defined = selectedClient.MarkupC2C !== null && selectedClient.MarkupC2C !== undefined;
        if (defined) {
          clientMarkupPercent = toPercentValue(selectedClient.MarkupC2C);
          hasDefinedMarkup = true;
        }
      } else if (data.employmentType === 'w2 hourly' || data.employmentType === 'w2 deel') {
        const defined = selectedClient.MarkupW2 !== null && selectedClient.MarkupW2 !== undefined;
        if (defined) {
          clientMarkupPercent = toPercentValue(selectedClient.MarkupW2);
          hasDefinedMarkup = true;
        }
      }
    }

    // Calculate Bill Rate from Pay Rate (if bill rate not manually entered)
    // Priority: 1) Allowed Markup % (no burden), 2) Target GP%, 3) Client markup if available
    if (payRate > 0 && billRate === 0) {
      if (allowedMarkup > 0) {
        // No-burden markup: Bill Rate = Pay Rate * (1 + Markup%), loaded cost = pay rate
        loadedCost = payRate;
        billRate = payRate * (1 + allowedMarkup / 100);
      } else if (targetGP > 0) {
        // Target GP% takes priority - calculate bill rate from GP%
        loadedCost = payRate * LOADED_COST_MULTIPLIER;
        billRate = loadedCost / (1 - targetGP / 100);
      } else if (hasDefinedMarkup && clientMarkupPercent !== null) {
        // Use client markup only when actually defined
        // Calculate loaded cost first to account for fees
        loadedCost = payRate * LOADED_COST_MULTIPLIER;
        // Apply markup to loaded cost (not just pay rate) to preserve intended GP%
        const markup = clientMarkupPercent / 100;
        billRate = loadedCost * (1 + markup);
      }
    }

    // Calculate Pay Rate from Bill Rate (if pay rate not manually entered)
    // Priority: 1) Allowed Markup % (no burden), 2) Target GP%, 3) Client markup if available
    if (billRate > 0 && payRate === 0) {
      if (allowedMarkup > 0) {
        // No-burden markup: Pay Rate = Bill Rate / (1 + Markup%), loaded cost = pay rate
        payRate = billRate / (1 + allowedMarkup / 100);
        loadedCost = payRate;
      } else if (targetGP > 0) {
        // Calculate pay rate from bill rate and GP%
        // GP% = (Bill Rate - Loaded Cost) / Bill Rate
        // Loaded Cost = Bill Rate * (1 - GP% / 100)
        // Pay Rate = Loaded Cost / LOADED_COST_MULTIPLIER
        if (LOADED_COST_MULTIPLIER > 0) {
          loadedCost = billRate * (1 - targetGP / 100);
          payRate = loadedCost / LOADED_COST_MULTIPLIER;
        }
      } else if (hasDefinedMarkup && clientMarkupPercent !== null) {
        // Use client markup to back-calculate pay rate
        // When using fixed markup, need to solve for pay rate where:
        // billRate = loadedCost * (1 + markup)
        // loadedCost = payRate * LOADED_COST_MULTIPLIER
        // Therefore: billRate = payRate * LOADED_COST_MULTIPLIER * (1 + markup)
        // So: payRate = billRate / (LOADED_COST_MULTIPLIER * (1 + markup))
        const markup = clientMarkupPercent / 100;
        if (LOADED_COST_MULTIPLIER > 0) {
          payRate = billRate / (LOADED_COST_MULTIPLIER * (1 + markup));
          loadedCost = payRate * LOADED_COST_MULTIPLIER;
        }
      }
    }

    // Recalculate loaded cost with final pay rate (only if payRate is valid)
    if (payRate > 0) {
      loadedCost = payRate * LOADED_COST_MULTIPLIER;
    }
    
    // Preserve manually entered values to prevent floating point precision errors
    // BUT: If we calculated payRate from Bill Rate + GP%, don't overwrite it with original (0)
    if (customMultiplier === null) {
      // Normal input changes - preserve original values
      if (originalBillRate > 0) {
        billRate = originalBillRate;
      }
      // Only preserve originalPayRate if it was actually entered (not calculated from Bill Rate + GP%)
      if (originalPayRate > 0) {
        payRate = originalPayRate;
        // Recalculate loaded cost with the preserved pay rate
        loadedCost = payRate * LOADED_COST_MULTIPLIER;
      }
      // If we calculated payRate from billRate + targetGP, don't overwrite it
    } else {
      // Loaded cost change - recalculate the rate that wasn't manually entered
      // If both rates were entered, preserve bill rate and recalculate everything else
      // If only pay rate was entered, preserve it and let loaded cost/bill rate adjust
      // If only bill rate was entered, preserve it and recalculate pay rate from GP% if available
      if (originalBillRate > 0 && originalPayRate > 0) {
        // Both entered - preserve bill rate, loaded cost will change, GP% will recalculate
        billRate = originalBillRate;
      } else if (originalPayRate > 0 && originalBillRate === 0) {
        // Only pay rate entered - preserve it, loaded cost already recalculated above
        payRate = originalPayRate;
      } else if (originalBillRate > 0 && originalPayRate === 0) {
        // Only bill rate entered - preserve it and recalculate pay rate from GP% or fixed markup
        billRate = originalBillRate;
        if (allowedMarkup > 0) {
          payRate = billRate / (1 + allowedMarkup / 100);
          loadedCost = payRate;
        } else if (targetGP > 0 && LOADED_COST_MULTIPLIER > 0) {
          // Recalculate pay rate to maintain the target GP%
          loadedCost = billRate * (1 - targetGP / 100);
          payRate = loadedCost / LOADED_COST_MULTIPLIER;
        } else if (hasDefinedMarkup && clientMarkupPercent !== null && LOADED_COST_MULTIPLIER > 0) {
          // Recalculate pay rate to maintain fixed markup when fees change
          const markup = clientMarkupPercent / 100;
          payRate = billRate / (LOADED_COST_MULTIPLIER * (1 + markup));
          loadedCost = payRate * LOADED_COST_MULTIPLIER;
        }
      }
    }

    // Calculate Markup % - use client markup only when defined; otherwise calculate from rates
    // But when Target GP% is set, always calculate from actual rates (not client markup)
    let markupPercent = 0;
    if (allowedMarkup > 0) {
      markupPercent = allowedMarkup;
    } else if (targetGP > 0) {
      // When target GP% is set, calculate markup from the resulting bill and pay rates
      if (payRate > 0 && billRate > 0) {
        markupPercent = ((billRate - payRate) / payRate) * 100;
      }
    } else if (hasDefinedMarkup && clientMarkupPercent !== null) {
      // Otherwise use client markup if defined
      markupPercent = clientMarkupPercent;
    } else if (payRate > 0 && billRate > 0) {
      // Fallback to calculating from rates
      markupPercent = ((billRate - payRate) / payRate) * 100;
    }

    // Calculate GP% = (Bill Rate - Loaded Cost) / Bill Rate × 100
    let gpPercent = 0;
    if (billRate > 0) {
      gpPercent = ((billRate - loadedCost) / billRate) * 100;
    }

    // Calculate gross profit (Bill Rate - Loaded Cost)
    const grossProfit = billRate - loadedCost;

    // GP per hour is the same as gross profit since bill rate and loaded cost are already hourly
    const gpPerHour = grossProfit;

    // Calculate potential monthly commission based on gross profit
    const hoursPerMonth = 40 * 4;
    const monthlyCommission = grossProfit * hoursPerMonth;

    // Apply discount
    const finalBillRate = billRate * (1 - discount / 100);

    // Guard against NaN values
    const safeValues = {
      monthlyCommission: isNaN(monthlyCommission) ? 0 : Math.round(monthlyCommission * 100) / 100,
      markupPercent: isNaN(markupPercent) ? 0 : Math.round(markupPercent * 100) / 100,
      billRateBeforeDiscount: isNaN(billRate) ? 0 : Math.round(billRate * 100) / 100,
      billRate: isNaN(finalBillRate) ? 0 : Math.round(finalBillRate * 100) / 100,
      payRate: isNaN(payRate) ? 0 : Math.round(payRate * 100) / 100,
      loadedCost: isNaN(loadedCost) ? 0 : Math.round(loadedCost * 100) / 100,
      gpPercent: isNaN(gpPercent) ? 0 : Math.round(gpPercent * 100) / 100,
      grossProfit: isNaN(grossProfit) ? 0 : Math.round(grossProfit * 100) / 100,
      gpPerHour: isNaN(gpPerHour) ? 0 : Math.round(gpPerHour * 100) / 100,
      hasDefinedMarkup: hasDefinedMarkup,
      targetGP: targetGP
    };

    setCalculations(safeValues);
  };

  // True when Target GP% is driving the calculation (manually entered or locked by the client record)
  const isTargetGpActive = () => {
    if (formData.targetGP !== '') return true;
    const client = clients.find(c => c.ClientId === parseInt(formData.client));
    if (!client) return false;
    if (client.SetGP != null && client.SetGP !== '') return true;
    if (
      (formData.employmentType === 'w2 hourly' || formData.employmentType === 'fulltime' || formData.employmentType === 'w2 deel') &&
      (client.MarkupW2 != null && client.MarkupW2 !== '')
    ) return true;
    if (
      (formData.employmentType === 'c2c' || formData.employmentType === 'c2c canada') &&
      (client.MarkupC2C != null && client.MarkupC2C !== '')
    ) return true;
    return false;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedData = { ...formData, [name]: value };

    // Target GP% and Allowed Markup% are mutually exclusive calculation modes
    if (name === 'targetGP' && value !== '') {
      updatedData.allowedMarkup = '';
    }
    if (name === 'allowedMarkup' && value !== '') {
      updatedData.targetGP = '';
    }

    // When client is selected, auto-populate discount from client's TotalDiscounts
    if (name === 'client' && value) {
      const selectedClient = clients.find(c => c.ClientId === parseInt(value));
      if (selectedClient && selectedClient.TotalDiscounts) {
        const discountPercent = toPercentValue(selectedClient.TotalDiscounts);
        updatedData.discount = discountPercent ?? '';
      }
    }

    // When switching to full-time, convert pay rate to annual salary
    if (name === 'employmentType' && value === 'fulltime') {
      const currentPayRate = parseFloat(formData.targetPayRate) || 0;
      if (currentPayRate > 0) {
        // Convert hourly rate to annual salary (2080 hours per year)
        updatedData.annualSalary = Math.round(currentPayRate * 2080);
      }
      updatedData.targetPayRate = '';
      // Reset loaded cost when switching employment type
      setLoadedCostMultiplier(1.18);
      setLoadedCostPercent(18);
      setLoadedCostFees([]);
    }
    
    // When switching to W2/Deel (Canada), switch currency to CAD and apply W2 CAD fees
    if (name === 'employmentType' && value === 'w2 deel') {
      setSelectedCurrency('CAD');
      setLoadedCostMultiplier(1.18);
      setLoadedCostPercent(18);
      // Fees will be applied via the CAD auto-sync effect
      setLoadedCostFees([]);
    }
    
    // When switching to C2C (Canada), switch currency to CAD
    if (name === 'employmentType' && value === 'c2c canada') {
      setSelectedCurrency('CAD');
      setLoadedCostMultiplier(1.0);
      setLoadedCostPercent(0);
      setLoadedCostFees([]);
    }
    
    // When switching away from C2C/Deel or W2/Deel to any other employment type, revert to USD
    if (name === 'employmentType' && (formData.employmentType === 'w2 deel' || formData.employmentType === 'c2c canada') && value !== 'w2 deel' && value !== 'c2c canada') {
      setSelectedCurrency('USD');
      setLoadedCostFees([]);
      if (value === 'c2c') {
        setLoadedCostMultiplier(1.0);
        setLoadedCostPercent(0);
      } else {
        setLoadedCostMultiplier(1.18);
        setLoadedCostPercent(18);
      }
    }
    
    // When switching from full-time to hourly, convert annual salary to pay rate
    if (name === 'employmentType' && value !== 'fulltime' && formData.employmentType === 'fulltime') {
      const currentSalary = parseFloat(formData.annualSalary) || 0;
      if (currentSalary > 0) {
        // Convert annual salary to hourly rate (2080 hours per year)
        updatedData.targetPayRate = (currentSalary / 2080).toFixed(2);
      }
      updatedData.annualSalary = '';
      // Reset loaded cost when switching employment type
      if (value === 'c2c' || value === 'c2c canada') {
        setLoadedCostMultiplier(1.0);
        setLoadedCostPercent(0);
      } else {
        setLoadedCostMultiplier(1.18);
        setLoadedCostPercent(18);
      }
      setLoadedCostFees([]);
    }
    
    // When switching between w2 and c2c directly (not through fulltime)
    if (name === 'employmentType' && formData.employmentType !== 'fulltime' && value !== 'fulltime' && formData.employmentType !== value) {
      if (value === 'c2c' || value === 'c2c deel' || value === 'c2c canada') {
        setLoadedCostMultiplier(1.0);
        setLoadedCostPercent(0);
        if (selectedCurrency === 'CAD') {
          setLoadedCostFees([]);
        }
      } else {
        setLoadedCostMultiplier(1.18);
        setLoadedCostPercent(18);
      }
      setLoadedCostFees([]);
    }

    // When client changes, clear annualSalary always.
    // Only clear targetGP if:
    //   - The new client has a C2C% or W2% for the current employment type, and
    //   - The employment type matches (C2C for c2c/c2c deel, W2 for w2 hourly/w2 deel/fulltime)
    // Otherwise, preserve manually entered targetGP.
    if (name === 'client') {
      updatedData.annualSalary = '';
      const prevClient = formData.client ? clients.find(c => c.ClientId === parseInt(formData.client)) : null;
      const newClient = value ? clients.find(c => c.ClientId === parseInt(value)) : null;
      const employmentType = formData.employmentType;
      // Determine if the new client has a markup for the current employment type
      let newClientHasRelevantMarkup = false;
      if (newClient) {
        if ((employmentType === 'c2c' || employmentType === 'c2c deel' || employmentType === 'c2c canada') && newClient.MarkupC2C) {
          newClientHasRelevantMarkup = true;
        } else if ((employmentType === 'w2 hourly' || employmentType === 'w2 deel' || employmentType === 'fulltime') && newClient.MarkupW2) {
          newClientHasRelevantMarkup = true;
        }
      }
      // Only clear if the new client has a relevant markup for the current employment type
      if (newClientHasRelevantMarkup) {
        updatedData.targetGP = '';
      }
    }
    
    setFormData(updatedData);
    calculateValues(updatedData);
  };

  const handleDiscountSelect = (e) => {
    const selectedDiscount = e.target.value;
    const updatedData = { ...formData, discount: selectedDiscount };
    setFormData(updatedData);
    calculateValues(updatedData);
  };

  // Insert or update a fee entry by name while preserving existing IDs
  const upsertFeeByName = (fees, name, amount) => {
    const sanitizedAmount = Number.isFinite(amount) ? amount : 0;
    const existingIndex = fees.findIndex(fee => fee.name === name);
    if (existingIndex !== -1) {
      const updatedFees = [...fees];
      updatedFees[existingIndex] = { ...updatedFees[existingIndex], amount: sanitizedAmount };
      return updatedFees;
    }
    const nextId = Math.max(...fees.map(fee => fee.id || 0), 0) + 1;
    return [{ id: nextId, name, amount: sanitizedAmount }, ...fees];
  };

  const getInputUSDNote = (value) => {
    if (selectedCurrency === 'USD' || value === '' || value === undefined) return null;
    const rate = exchangeRates[selectedCurrency] || 1;
    const usdVal = parseFloat(value) / rate;
    if (isNaN(usdVal)) return null;
    return `USD $${usdVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatCurrencyValue = (value) => {
    const rate = exchangeRates[selectedCurrency] !== undefined ? exchangeRates[selectedCurrency] : 1;
    const convertedValue = value * rate;
    const currencySymbols = { USD: '$', CAD: 'C$', INR: '₹' };
    const symbol = currencySymbols[selectedCurrency] || '$';
    
    console.log(`Currency: ${selectedCurrency}, Rate: ${rate}, Value: ${value}, Converted: ${convertedValue}`);
    return `${symbol}${convertedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getUSDEquivalent = (value) => {
    if (selectedCurrency === 'USD') return null;
    return `USD $${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  useEffect(() => {
    if (selectedCurrency !== 'CAD') return;
    if (formData.employmentType === 'c2c' || formData.employmentType === 'c2c deel' || formData.employmentType === 'c2c canada') {
      setLoadedCostFees((prevFees) => {
        const nonAutoFees = prevFees.filter(fee => !['Deel Fee', 'Unbillable PTO', 'Employer Costs', 'Benefit Admin Fee'].includes(fee.name));
        const hasDeelFee = nonAutoFees.some(fee => fee.name === 'Deel Fee');
        if (!hasDeelFee) {
          const nextId = Math.max(...nonAutoFees.map(f => f.id || 0), 0) + 1;
          return [{ id: nextId, name: 'Deel Fee', amount: 599 * 12 }, ...nonAutoFees];
        }
        return nonAutoFees;
      });
      return;
    }
    // Prevent loop: only update if fees are not already set as expected
    const cadToUsdRate = exchangeRates.CAD ? 1 / exchangeRates.CAD : 1;
    const defaultAnnualHours = 2080;
    const hoursPerDay = defaultAnnualHours / 260;
    const payRateForFees = parseFloat(formData.targetPayRate) || calculations.payRate || 0;
    const ptoAnnualAmount = payRateForFees > 0 ? payRateForFees * hoursPerDay * 10 : 0;
    const employerCostsAnnual = 646.35 * cadToUsdRate * 5;
    const benefitAdminAnnual = 35.29 * cadToUsdRate * 5;
    const expectedFees = [
      { name: 'Deel Fee', amount: 599 * 12 },
      { name: 'Unbillable PTO', amount: ptoAnnualAmount },
      { name: 'Employer Costs', amount: employerCostsAnnual },
      { name: 'Benefit Admin Fee', amount: benefitAdminAnnual }
    ];
    setLoadedCostFees((prevFees) => {
      // If all expected fees are present and amounts match, do not update
      const allMatch = expectedFees.every(ef => {
        const found = prevFees.find(f => f.name === ef.name);
        return found && Math.abs(found.amount - ef.amount) < 0.01;
      });
      if (allMatch && prevFees.length === expectedFees.length) return prevFees;
      // Otherwise, update
      let updatedFees = prevFees;
      expectedFees.forEach(ef => {
        updatedFees = upsertFeeByName(updatedFees, ef.name, ef.amount);
      });
      // Remove any extra auto fees
      updatedFees = updatedFees.filter(f => expectedFees.some(ef => ef.name === f.name));
      return updatedFees;
    });
  }, [selectedCurrency, calculations.payRate, formData.targetPayRate, exchangeRates.CAD, formData.employmentType]);

  if (loading) return <div className="loading">Loading calculator...</div>;

  const selectedClient = formData.client ? clients.find(c => c.ClientId === parseInt(formData.client)) : null;

  return (
    <div className="container">
      <h1>Rate Calculator</h1>

      <div className="calculator-wrapper">
        <div className="calculator-inputs card">
          <h2>Input Parameters</h2>
          <form className="calculator-form">
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <label htmlFor="client">Client *</label>
                {canManageClients && (
                  <button
                    type="button"
                    onClick={() => setShowClientModal(true)}
                    className="client-management-link"
                  >
                    Manage Clients
                  </button>
                )}
              </div>
              <select
                id="client"
                name="client"
                value={formData.client}
                onChange={e => {
                  handleInputChange(e);
                  const selectedId = e.target.value;
                  const selectedClient = clients.find(c => c.ClientId === parseInt(selectedId));
                  if (
                    !selectedId ||
                    (selectedClient && selectedClient.Title &&
                      (selectedClient.Title.toLowerCase().includes('custom client') || selectedClient.Title.toLowerCase().includes('*custom')))
                  ) {
                    setFormData(prev => ({ ...prev, discount: '' }));
                  }
                }}
                required
              >
                <option value="">Select a client...</option>
                {clients.map((client) => (
                  <option key={client.ClientId} value={client.ClientId}>
                    {client.Title}
                  </option>
                ))}
              </select>
            </div>

            {/* Compact client info display */}
            {formData.client && (() => {
              const client = clients.find(c => c.ClientId === parseInt(formData.client));
              if (!client) return null;
              return (
                <div className="client-info-compact">
                  {client.MSP && <span className="info-tag"><strong>MSP:</strong> {client.MSP}</span>}
                  {client.Tool && <span className="info-tag"><strong>Tool:</strong> {client.Tool}</span>}
                  {client.MSPFee !== null && <span className="info-tag"><strong>MSP Fee:</strong> {formatPercentDisplay(client.MSPFee)}</span>}
                  {client.TotalDiscounts !== null && <span className="info-tag"><strong>Discount:</strong> {formatPercentDisplay(client.TotalDiscounts)}</span>}
                  {client.MarkupW2 !== null && <span className="info-tag"><strong>W2%:</strong> {formatPercentDisplay(client.MarkupW2)}</span>}
                  {client.MarkupC2C !== null && <span className="info-tag"><strong>C2C%:</strong> {formatPercentDisplay(client.MarkupC2C)}</span>}
                  {client.C2CFriendly !== null && <span className="info-tag"><strong>C2C Friendly:</strong> {client.C2CFriendly ? 'Yes' : 'No'}</span>}
                  {client.StartingFee !== null && <span className="info-tag"><strong>Starting Fee:</strong> {formatPercentDisplay(client.StartingFee)}</span>}
                  {client.ConversionMaxLength !== null && <span className="info-tag"><strong>Conv Max:</strong> {client.ConversionMaxLength} mo</span>}
                  {client.OTRate !== null && <span className="info-tag"><strong>OT:</strong> {client.OTRate}x</span>}
                </div>
              );
            })()}

            <div className="form-group">
              <label htmlFor="employmentType">Employment Type *</label>
              <select
                id="employmentType"
                name="employmentType"
                value={formData.employmentType}
                onChange={handleInputChange}
                required
              >
                <option value="c2c">C2C (Contractor)</option>
                <option value="c2c canada">C2C (Canada)</option>
                <option value="w2 hourly">W2 Hourly</option>
                <option value="fulltime">Full-Time</option>
                <option value="w2 deel">W2/Deel (Canada)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="discount">Discount %</label>
              <div className="discount-input-group">
                <input
                  type="number"
                  id="discount"
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  placeholder="Enter discount %"
                  min="0"
                  max="100"
                  step="0.1"
                  disabled={(() => {
                    const client = clients.find(c => c.ClientId === parseInt(formData.client));
                    return !!(client && client.TotalDiscounts != null && client.TotalDiscounts !== '');
                  })()}
                />
                {discounts.length > 0 && (
                  <>
                    <span className="separator">or</span>
                    <select
                      className="discount-select"
                      value=""
                      onChange={handleDiscountSelect}
                    >
                      <option value="">Quick Select...</option>
                      {discounts.map((discount) => (
                        <option key={discount.id} value={discount.value}>
                          {discount.name} ({discount.value}%)
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="targetBillRate">Target Bill Rate ({{ USD: '$', CAD: 'C$', INR: '₹' }[selectedCurrency] || '$'})</label>
              <input
                type="number"
                id="targetBillRate"
                name="targetBillRate"
                value={formData.targetBillRate}
                onChange={handleInputChange}
                placeholder="Enter bill rate (or leave blank to calculate)"
                min="0"
                step="0.01"
              />
              {getInputUSDNote(formData.targetBillRate) && (
                <small style={{ color: '#666' }}>≈ {getInputUSDNote(formData.targetBillRate)} USD</small>
              )}
            </div>

            {formData.employmentType === 'fulltime' ? (
              <div className="form-group">
                <label htmlFor="annualSalary">Annual Salary ({{ USD: '$', CAD: 'C$', INR: '₹' }[selectedCurrency] || '$'})</label>
                <input
                  type="number"
                  id="annualSalary"
                  name="annualSalary"
                  value={formData.annualSalary}
                  onChange={handleInputChange}
                  placeholder="Enter annual salary"
                  min="0"
                  step="0.01"
                />
                {getInputUSDNote(formData.annualSalary) && (
                  <small style={{ color: '#666' }}>≈ {getInputUSDNote(formData.annualSalary)} USD</small>
                )}
                <small>Hourly rate = Annual Salary ÷ 2,080 hours</small>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="targetPayRate">Target Pay Rate ({{ USD: '$', CAD: 'C$', INR: '₹' }[selectedCurrency] || '$'})</label>
                <input
                  type="number"
                  id="targetPayRate"
                  name="targetPayRate"
                  value={formData.targetPayRate}
                  onChange={handleInputChange}
                  placeholder="Enter pay rate (or leave blank to calculate)"
                  min="0"
                  step="0.01"
                />
                {getInputUSDNote(formData.targetPayRate) && (
                  <small style={{ color: '#666' }}>≈ {getInputUSDNote(formData.targetPayRate)} USD</small>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="targetGP">Target GP % (Gross Profit %)</label>
              <input
                type="number"
                id="targetGP"
                name="targetGP"
                value={(() => {
                  if (formData.allowedMarkup !== '') {
                    return Number.isFinite(calculations.gpPercent) ? Math.round(calculations.gpPercent * 100) / 100 : '';
                  }
                  const client = clients.find(c => c.ClientId === parseInt(formData.client));
                  const toPercent = (val) => {
                    const num = parseFloat(val);
                    if (!Number.isFinite(num)) return '';
                    return num < 1 ? Math.round(num * 100) : Math.round(num);
                  };
                  if (!client) return formData.targetGP;
                  // If locked, show the correct value as whole %
                  if (client.SetGP != null && client.SetGP !== '') return toPercent(client.SetGP);
                  if (formData.employmentType && client.MarkupW2 != null && client.MarkupW2 !== '' && (formData.employmentType === 'w2 hourly' || formData.employmentType === 'fulltime' || formData.employmentType === 'w2 deel')) return toPercent(client.MarkupW2);
                  if (formData.employmentType && client.MarkupC2C != null && client.MarkupC2C !== '' && (formData.employmentType === 'c2c' || formData.employmentType === 'c2c canada')) return toPercent(client.MarkupC2C);
                  return formData.targetGP;
                })()}
                onChange={handleInputChange}
                placeholder="Enter target GP %"
                min="0"
                max="100"
                step="0.1"
                disabled={(() => {
                  const client = clients.find(c => c.ClientId === parseInt(formData.client));
                  if (formData.allowedMarkup !== '') return true;
                  if (!client) return false;
                  if (client.SetGP != null && client.SetGP !== '') return true;
                  if (
                    (formData.employmentType === 'w2 hourly' || formData.employmentType === 'fulltime' || formData.employmentType === 'w2 deel') &&
                    (client.MarkupW2 != null && client.MarkupW2 !== '')
                  ) return true;
                  if (
                    (formData.employmentType === 'c2c' || formData.employmentType === 'c2c canada') &&
                    (client.MarkupC2C != null && client.MarkupC2C !== '')
                  ) return true;
                  return false;
                })()}
              />
            </div>

            <div className="form-group">
              <label htmlFor="allowedMarkup">Allowed Markup % (No Burden)</label>
              <input
                type="number"
                id="allowedMarkup"
                name="allowedMarkup"
                value={isTargetGpActive() ? (Number.isFinite(calculations.markupPercent) ? Math.round(calculations.markupPercent * 100) / 100 : '') : formData.allowedMarkup}
                onChange={handleInputChange}
                placeholder="Enter allowed markup %"
                min="0"
                step="0.1"
                disabled={isTargetGpActive()}
              />
              
            </div>
          </form>
        </div>

        <div className="calculator-outputs card">
          <div className="results-header">
            <h2>Calculated Results</h2>
            <div className="currency-selector">
              <label htmlFor="currency">Currency:</label>
              <select
                id="currency"
                value={selectedCurrency}
                onChange={(e) => {
                  const newCurrency = e.target.value;
                  const oldRate = exchangeRates[selectedCurrency] || 1;
                  const newRate = exchangeRates[newCurrency] || 1;
                  const conversionFactor = newRate / oldRate;

                  // Convert any entered input values to the new currency
                  const convertedForm = { ...formData };
                  if (formData.targetBillRate !== '') {
                    convertedForm.targetBillRate = (parseFloat(formData.targetBillRate) * conversionFactor).toFixed(2);
                  }
                  if (formData.targetPayRate !== '') {
                    convertedForm.targetPayRate = (parseFloat(formData.targetPayRate) * conversionFactor).toFixed(2);
                  }
                  if (formData.annualSalary !== '') {
                    convertedForm.annualSalary = (parseFloat(formData.annualSalary) * conversionFactor).toFixed(2);
                  }
                  setFormData(convertedForm);

                  setSelectedCurrency(newCurrency);
                  const cadToUsdRate = exchangeRates.CAD ? 1 / exchangeRates.CAD : 1;
                  const defaultAnnualHours = 2080;
                  const hoursPerDay = defaultAnnualHours / 260;
                  const payRateForFees = parseFloat(formData.targetPayRate) || calculations.payRate || 0;
                  const ptoAnnualAmount = payRateForFees > 0 ? payRateForFees * hoursPerDay * 10 : 0;
                  const employerCostsAnnual = 646.35 * cadToUsdRate * 5;
                  const benefitAdminAnnual = 35.29 * cadToUsdRate * 5;
                  
                  if (newCurrency === 'CAD') {
                    if (formData.employmentType === 'c2c' || formData.employmentType === 'c2c deel' || formData.employmentType === 'c2c canada') {
                      // C2C or C2C/Deel: only Deel Fee
                      setLoadedCostFees((prevFees) => {
                        const nonAutoFees = prevFees.filter(fee => !['Deel Fee', 'Unbillable PTO', 'Employer Costs', 'Benefit Admin Fee'].includes(fee.name));
                        const hasDeelFee = nonAutoFees.some(fee => fee.name === 'Deel Fee');
                        if (!hasDeelFee) {
                          const nextId = Math.max(...nonAutoFees.map(f => f.id || 0), 0) + 1;
                          return [{ id: nextId, name: 'Deel Fee', amount: 599 * 12 }, ...nonAutoFees];
                        }
                        return nonAutoFees;
                      });
                    } else {
                      const withDeelFee = upsertFeeByName(loadedCostFees, 'Deel Fee', 599 * 12);
                      const withPto = upsertFeeByName(withDeelFee, 'Unbillable PTO', ptoAnnualAmount);
                      const withEmployer = upsertFeeByName(withPto, 'Employer Costs', employerCostsAnnual);
                      const withBenefit = upsertFeeByName(withEmployer, 'Benefit Admin Fee', benefitAdminAnnual);
                      setLoadedCostFees(withBenefit);
                    }
                  } else {
                    const autoFeeNames = ['Deel Fee', 'Unbillable PTO', 'Employer Costs', 'Benefit Admin Fee'];
                    const filteredFees = loadedCostFees.filter(fee => !autoFeeNames.includes(fee.name));
                    setLoadedCostFees(filteredFees);
                    
                    // Reset loaded cost multiplier/percent based on employment type
                    if (formData.employmentType === 'c2c' || formData.employmentType === 'c2c deel' || formData.employmentType === 'c2c canada') {
                      setLoadedCostMultiplier(1.0);
                      setLoadedCostPercent(0);
                    } else {
                      // W2/fulltime keep their 18% base
                      setLoadedCostMultiplier(1.18);
                      setLoadedCostPercent(18);
                    }
                    setLoadedCostComponents({
                      pto: 0,
                      benefits: 0,
                      taxes: 0,
                      h1bTransferFees: 0,
                      otherFees: 0
                    });
                  }
                }}
              >
                <option value="USD">USD ($) - 1.0</option>
                <option value="CAD">CAD (C$) - {exchangeRates.CAD.toFixed(2)}</option>
                {/* <option value="INR">INR (₹) - {exchangeRates.INR.toFixed(2)}</option> */}
              </select>
              <button 
                type="button" 
                className="refresh-rates-btn"
                onClick={fetchExchangeRates}
                disabled={loadingRates}
                title="Refresh exchange rates"
              >
                {loadingRates ? 'Updating...' : '↻'}
              </button>
            </div>
          </div>
          <div className="results-grid">
            {/* Removed Potential Monthly Revenue and Markup % */}
          {/* Commission Details button moved below results grid, above notes */}

            {/* Only show a single Bill Rate field. Add (after discount) if Discount% > 0 */}
            <div className="result-item" style={getHighlightedResultStyle('billRate')}>
              <label>
                Bill Rate
                {parseFloat(formData.discountPercent) > 0 && ' (after discount)'}
              </label>
              <div className="result-value" style={{ ...getResultValueStyle(calculations.billRate), ...getHighlightedResultValueColor('billRate') }}>{formatCurrencyValue(calculations.billRate)}</div>
              {getUSDEquivalent(calculations.billRate) && (
                <div style={{ fontSize: '0.75em', color: '#666', marginTop: '2px' }}>{getUSDEquivalent(calculations.billRate)}</div>
              )}
            </div>

            <div className="result-item" style={getHighlightedResultStyle('payRate')}>
              <label>
                Pay Rate
                {formData.employmentType === 'fulltime' && (
                  <span style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                    {' '}(
                    {formData.annualSalary 
                      ? `${formatCurrencyValue(parseFloat(formData.annualSalary))} annual`
                      : `${formatCurrencyValue(calculations.payRate * 2080)} annual`
                    })
                  </span>
                )}
              </label>
              <div className="result-value" style={{ ...getResultValueStyle(calculations.payRate), ...getHighlightedResultValueColor('payRate') }}>{formatCurrencyValue(calculations.payRate)}</div>
              {getUSDEquivalent(calculations.payRate) && (
                <div style={{ fontSize: '0.75em', color: '#666', marginTop: '2px' }}>{getUSDEquivalent(calculations.payRate)}</div>
              )}
            </div>

            <div className="result-item" style={getHighlightedResultStyle('loadedCost')}>
              <label>
                {(() => {
                  // Allowed Markup% is a no-burden mode; loaded cost fees are ignored regardless of employment type
                  if (formData.allowedMarkup !== '') {
                    return <>Loaded Cost (no burden)</>;
                  }
                  // Calculate the actual percentage being displayed
                  let displayPercent = loadedCostPercent;
                  if ((formData.employmentType === 'w2 hourly' || formData.employmentType === 'fulltime' || formData.employmentType === 'w2 deel') && loadedCostMultiplier <= 1.0) {
                    displayPercent = 18; // Show W2 default
                  }
                  return displayPercent === 0 ? (
                    <>Loaded Cost</>
                  ) : (
                    <>Loaded Cost (+{displayPercent.toFixed(1)}%)</>
                  );
                })()}
                <button
                  type="button"
                  className="edit-loaded-cost-btn"
                  onClick={() => setShowLoadedCostModal(true)}
                  title="Edit loaded cost components"
                >
                  Cost Sheet
                </button>
              </label>
              <div className="result-value" style={{ ...getResultValueStyle(calculations.loadedCost), ...getHighlightedResultValueColor('loadedCost') }}>{formatCurrencyValue(calculations.loadedCost)}</div>
              {getUSDEquivalent(calculations.loadedCost) && (
                <div style={{ fontSize: '0.75em', color: '#666', marginTop: '2px' }}>{getUSDEquivalent(calculations.loadedCost)}</div>
              )}
              {loadedCostFees.length > 0 && (
                (() => {
                  const customFees = loadedCostFees.filter(f => f.name && f.amount > 0);
                  if (customFees.length === 0) return null;
                  return (
                    <div style={{ fontSize: '0.75em', color: '#0078d4', marginTop: '4px', fontWeight: '500' }}>
                      {customFees.map((fee, idx) => {
                        const hourlyAmount = (fee.amount / 2080).toFixed(2);
                        const monthlyAmount = (fee.amount / 12).toFixed(2);
                        return (
                          <div key={idx}>
                            + {fee.name}: ${hourlyAmount}/hr (${monthlyAmount}/month)
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>

            <div className="result-item" style={getHighlightedResultStyle('gpPercent')}>
              <label>GP %</label>
              <div className="result-value" style={{ ...getResultValueStyle(calculations.gpPercent), ...getHighlightedResultValueColor('gpPercent') }}>{calculations.gpPercent.toFixed(2)}%</div>
            </div>

            <div className="result-item" style={getHighlightedResultStyle('grossProfit')}>
              <label>Gross Profit per Hour</label>
              <div className="result-value" style={{ ...getResultValueStyle(calculations.gpPerHour), ...getHighlightedResultValueColor('grossProfit') }}>{formatCurrencyValue(calculations.gpPerHour)}</div>
              {getUSDEquivalent(calculations.gpPerHour) && (
                <div style={{ fontSize: '0.75em', color: '#666', marginTop: '2px' }}>{getUSDEquivalent(calculations.gpPerHour)}</div>
              )}
            </div>
          </div>

          <div style={{ textAlign: 'center', margin: '1.5em 0 0.5em 0' }}>
            <button
              type="button"
              className="primary-action-btn"
              style={{ minWidth: 180, fontSize: '1rem', padding: '10px 24px', borderRadius: '6px', background: '#0078d4', color: 'white', border: 'none', fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', cursor: 'pointer', transition: 'background 0.2s' }}
              onClick={() => setShowCommissionModal(true)}
              onMouseOver={e => e.currentTarget.style.background = '#005a9e'}
              onMouseOut={e => e.currentTarget.style.background = '#0078d4'}
            >
              Commission Details
            </button>
          </div>
          {/* Calculation Logic link and modal */}
          <div style={{ textAlign: 'center', margin: '10em 0 0.5em 0' }}>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: '#0078d4', textDecoration: 'underline', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}
              onClick={() => setShowNotesModal(true)}
            >
              Calculation Logic
            </button>
          </div>
          <CalculationNotesModal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} />
          
        </div>
      </div>

      <ClientModal
        isOpen={showClientModal}
        onClose={handleClientModalClose}
        onClientCreated={loadClientAndDiscountData}
      />

      <CommissionModal
        isOpen={showCommissionModal}
        onClose={() => setShowCommissionModal(false)}
        gpPerHour={calculations.grossProfit}
      />

      <LoadedCostModal
        isOpen={showLoadedCostModal}
        onClose={() => setShowLoadedCostModal(false)}
        currentMultiplier={
          (formData.employmentType === 'w2 hourly' || formData.employmentType === 'fulltime' || formData.employmentType === 'w2 deel') && loadedCostMultiplier <= 1.0
            ? 1.18  // W2 default
            : loadedCostMultiplier
        }
        onSave={handleLoadedCostSave}
        basePayRate={calculations.payRate}
        employmentType={formData.employmentType}
        initialFees={loadedCostFees}
        currency={selectedCurrency}
        cadToUsdRate={exchangeRates.CAD ? 1 / exchangeRates.CAD : 1}
      />
    </div>
  );
};

const RateCalculatorWithProtection = () => (
  <ProtectedRoute requiredTab="rate-calculator">
    <RateCalculator />
  </ProtectedRoute>
);

export default RateCalculatorWithProtection;
